import { useCallback } from 'react'

import {
  useStoreKeyEquals,
  useStoreKeySelector,
} from '@/lib/valtio/store-hooks'

import { ColumnType } from '~shared/rundown/column'
import type { Cue } from '~shared/rundown/cue'

import { readColumnAccess } from '../column/column-access'
import { useCue } from '../cue/cue.hooks'
import { useColumnSnapshot } from '../data/columns.store'
import { gridState } from '../grid/grid.store'

import { useCellKey } from './cell.context'
import { parseCellKey } from './cell.schema'

export function useCellCue(): Cue {
  const cellKey = useCellKey()
  const { cueId } = parseCellKey(cellKey)

  const cue = useCue(cueId)

  if (!cue) throw new Error(`Parent Cue not found for Cell: ${cellKey}`)
  return cue
}

export function useCellColumn() {
  const cellKey = useCellKey()
  const { columnId } = parseCellKey(cellKey)

  const column = useColumnSnapshot(columnId)

  if (!column) throw new Error(`Parent Column not found for Cell: ${cellKey}`)
  return column
}

export function useCellIsInPrivateNotesColumn(): boolean {
  const cellKey = useCellKey()
  const { columnId } = parseCellKey(cellKey)

  const column = useColumnSnapshot(columnId)

  if (!column) throw new Error(`Parent Column not found for Cell: ${cellKey}`)

  if (column.type !== ColumnType.RICHTEXT) return false // Perf: Skip check for non-text columns

  return readColumnAccess(column).isOwnPrivate
}

export function useCellIsReadOnly(): boolean {
  const cellKey = useCellKey()
  const { cueId, columnId } = parseCellKey(cellKey)

  const cue = useCue(cueId)
  const column = useColumnSnapshot(columnId)

  if (!cue || !column)
    throw new Error(`Parent Cue/Column not found for Cell: ${cellKey}`)

  const access = readColumnAccess(column)

  if (access.isOwnPrivate) return false
  if (cue.settings.preventEdits) return true

  return !access.canEditCells
}

export function useCellIsSelected(): boolean {
  const cellKey = useCellKey()

  return useStoreKeySelector(
    gridState,
    'selection',
    (selection) => selection?.type === 'cell' && selection.cellKey === cellKey,
  )
}

export function useCellIsEditing(): boolean {
  const cellKey = useCellKey()

  return useStoreKeyEquals(gridState, 'editingCell', cellKey)
}

/**
 * Controller that listens for clicks and determines if they occurred outside of
 * the editable cell. If it detect an intentional click outside, the handler is
 * called.
 *
 * It was designed as a replacement for the `onBlur` event on the Tiptap editor.
 * It was too easily triggered when clicking draggable items or other UI.
 *
 * Note: It was initially a simple hook, but was generalized because it
 * temporarily also has to work in the <SelectColumnCell/>, so it can have a
 * similar behaviour. Once the other cell type becomes a custom node, this can
 * be simplified.
 */
export function CellClickOutsideController({
  handler,
}: {
  handler: (event: MouseEvent | TouchEvent) => void
}) {
  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return

      const parentElm = node.parentElement
      if (!parentElm) return

      const listener = (event: MouseEvent | TouchEvent) => {
        if (!(event.target instanceof HTMLElement)) return

        const clickedInside = parentElm.contains(event.target as Node)
        const clickedDraggable = event.target.closest('[draggable="true"]') // Content nodes
        const clickedSuggestion = event.target.closest('.react-renderer') // Tiptap suggestions list
        const clickedToolbar = event.target.closest('[role="toolbar"]') // Cell toolbar
        const clickedOtherUI = event.target.closest('.backdrop-blur-xs') // Modal
        const clickedBaseUI = event.target.closest('[data-slot]') // Any BaseIO component: popover, menu, tooltip, etc.
        const clickedArkUI = event.target.closest('[data-scope]') // Any ArkUI component: popover, menu, tooltip, etc.

        // Special case for ArkUI dialog and backdrop
        const isArkUIDialogOpen = document.querySelector(
          '[data-scope="dialog"][data-state="open"]',
        )

        if (
          !clickedInside &&
          !clickedDraggable &&
          !clickedToolbar &&
          !clickedSuggestion &&
          !clickedOtherUI &&
          !clickedBaseUI &&
          !clickedArkUI &&
          !isArkUIDialogOpen
        ) {
          // Call handler
          handler(event)
        }
      }

      document.addEventListener('mousedown', listener)
      document.addEventListener('touchstart', listener)

      return () => {
        document.removeEventListener('mousedown', listener)
        document.removeEventListener('touchstart', listener)
      }
    },
    [handler],
  )

  return <div ref={refCallback}></div>
}
